import React from 'react';

import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { subYears } from 'date-fns';
import { Box, Grid, Grow, Paper, Typography } from '@mui/material';
import Http from '@/services/http';
import Loading from '@/components/loading';

import { DashboardFilter, IdentifiedWeaknessCategory } from '.';

interface ListIdentifiedWeaknessesByCategoryProps {
  category: IdentifiedWeaknessCategory;
  filter: DashboardFilter | null;
}

type ListIdentifiedWeaknessesByCategoryState = {
  name: string;
  code: string;
  proportion: number;
  totalEvents?: number;
};

const BAR_COLOR = '#4CAF7D';
const TOTAL_COLOR = '#1A3B5D';

type WaterfallEntry = {
  name: string;
  offset: number;
  value: number;
  proportion: number;
  totalEvents?: number;
  isTotal: boolean;
};

const formatPercentage = (value: number | string) => {
  const number = Number(value);
  if (Number.isNaN(number)) return '-';
  return `${number.toFixed(2)}%`;
};

function buildWaterfallData(
  items: ListIdentifiedWeaknessesByCategoryState[]
): WaterfallEntry[] {
  const sorted = [...items]
    .filter((c) => c.proportion > 0)
    .sort((a, b) => a.proportion - b.proportion);
  const total = sorted.reduce((sum, c) => sum + c.proportion, 0);

  let cumulative = 0;
  const entries: WaterfallEntry[] = sorted.map((c) => {
    const entry: WaterfallEntry = {
      name: c.name,
      offset: cumulative,
      value: c.proportion,
      proportion: c.proportion,
      totalEvents: c.totalEvents,
      isTotal: false,
    };
    cumulative += c.proportion;
    return entry;
  });

  entries.push({
    name: 'Total',
    offset: 0,
    value: total,
    proportion: total,
    isTotal: true,
  });

  return entries;
}

function renderBarLabel(waterfallEntries: WaterfallEntry[]) {
  return (props: any) => {
    const { x, y, width, index } = props;
    const entry = waterfallEntries[index];
    if (!entry) return null;
    const label = `${entry.proportion.toFixed(1)}%`;
    const fill = entry.isTotal ? '#fff' : '#1A3B5D';
    return (
      <text
        dominantBaseline="middle"
        fill={fill}
        fontSize={11}
        fontWeight="bold"
        textAnchor="middle"
        x={x + width / 2}
        y={y + 14}
      >
        {label}
      </text>
    );
  };
}

const CustomTooltip = (props: any) => {
  if (props.active && props.payload && props.payload.length) {
    const entry: WaterfallEntry = props.payload[0].payload;
    return (
      <Box>
        <Paper sx={{ p: 1 }}>
          <Typography variant="subtitle2">{entry.name}</Typography>
          <Typography>
            {entry.isTotal ? 'Total' : 'Proporção'}:{' '}
            {formatPercentage(entry.proportion)}
          </Typography>
          {!entry.isTotal && (
            <Typography>
              Total de eventos: {entry.totalEvents ?? '0'}
            </Typography>
          )}
        </Paper>
      </Box>
    );
  }
  return null;
};

export const ListIdentifiedWeaknessesByCategory: React.FC<
  ListIdentifiedWeaknessesByCategoryProps
> = ({ category, filter }) => {
  const [categoryStats, setCategoryStats] = React.useState<
    ListIdentifiedWeaknessesByCategoryState[] | null
  >(null);
  const [loading, setLoading] = React.useState(false);

  const defaultStartDate = React.useMemo(() => subYears(new Date(), 1), []);
  const defaultEndDate = React.useMemo(() => new Date(), []);

  const fetchIdentifiedWeaknessByCategory = React.useCallback(async () => {
    setLoading(true);
    const startDate =
      filter?.startDate?.toISOString() || defaultStartDate.toISOString();
    const endDate =
      filter?.endDate?.toISOString() || defaultEndDate.toISOString();
    const { data } = await Http.get(
      `dashboard/identified-category-weaknesses?category=${category?.category}&startDate=${startDate}&endDate=${endDate}`
    );
    setCategoryStats(
      data
        ?.filter(
          (item: ListIdentifiedWeaknessesByCategoryState) => item.proportion > 0
        )
        .sort(
          (
            a: ListIdentifiedWeaknessesByCategoryState,
            b: ListIdentifiedWeaknessesByCategoryState
          ) => {
            if (a.proportion < b.proportion) {
              return 1;
            }
            if (a.proportion > b.proportion) {
              return -1;
            }
            return 0;
          }
        ) ?? []
    );
    setLoading(false);
  }, [category, filter, defaultStartDate, defaultEndDate]);

  React.useEffect(() => {
    fetchIdentifiedWeaknessByCategory();
  }, [fetchIdentifiedWeaknessByCategory]);

  const shouldRenderChart = categoryStats && categoryStats?.length && !loading;
  const waterfallData = shouldRenderChart
    ? buildWaterfallData(categoryStats)
    : [];
  const barWidth = Math.max(
    40,
    Math.min(60, 600 / (waterfallData.length || 1))
  );

  return (
    <Grow in unmountOnExit>
      <Grid
        container
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(480px, auto))',
        }}
        sx={{ mt: 2, gap: 2, width: '100%', ml: 0 }}
      >
        <Loading backgroundColor="transparent" loading={loading} />
        {shouldRenderChart && (
          <Paper sx={{ p: 2, width: '100%', gridColumn: '1/-1' }}>
            <Typography sx={{ mb: 2, fontWeight: 'bold' }} variant="h6">
              {category.category}
            </Typography>
            <ResponsiveContainer height={420} width="100%">
              <BarChart
                barSize={barWidth}
                data={waterfallData}
                margin={{ top: 20, right: 30, left: 10, bottom: 120 }}
              >
                <CartesianGrid stroke="#f5f5f5" vertical={false} />
                <XAxis
                  angle={-35}
                  dataKey="name"
                  interval={0}
                  textAnchor="end"
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                />
                <YAxis tickFormatter={(v) => `${v.toFixed(0)}%`} />
                <Tooltip content={<CustomTooltip />} />
                {/* Transparent offset bar (base of waterfall) */}
                <Bar dataKey="offset" fill="transparent" stackId="waterfall">
                  {waterfallData.map((entry) => (
                    <Cell
                      fill="transparent"
                      key={`offset-${entry.name}`}
                      stroke="transparent"
                    />
                  ))}
                </Bar>
                {/* Visible value bar */}
                <Bar dataKey="value" name="Proporção" stackId="waterfall">
                  <LabelList
                    content={renderBarLabel(waterfallData)}
                    dataKey="proportion"
                    position="top"
                  />
                  {waterfallData.map((entry) => (
                    <Cell
                      fill={entry.isTotal ? TOTAL_COLOR : BAR_COLOR}
                      key={`bar-${entry.name}`}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            {!shouldRenderChart && !loading && (
              <span>
                Nenhum resultado para mostrar com o filtro selecionado.
              </span>
            )}
          </Paper>
        )}
      </Grid>
    </Grow>
  );
};
